import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useMobileNav } from '../hooks/useMobileNav';
import StaffProfileTab from '../components/StaffProfileTab';
import TimesheetView from '../components/TimesheetView';
import AvailabilityEditor from '../components/AvailabilityEditor';
import LeaveManager from '../components/LeaveManager';
import ShiftRequestsInbox from '../components/ShiftRequestsInbox';
import { ComplianceManager } from '../components/ComplianceManager';
import { DailyRoster } from '../components/DailyRoster';
import { StaffIDCardModal } from '../components/StaffIDCardModal';
import { MessageComposer } from '../components/MessageComposer';
import { MessagesInbox } from '../components/MessagesInbox';
// ClockInOutFAB was removed from this dashboard — see note near the end
// of the render tree. ClockStatusCard stays (it's mounted inside the
// Clock tab itself and is the canonical clock-in UI for staff).
import { ClockStatusCard } from '../components/ClockStatusCard';
import { MyPayslipsCard } from '../components/MyPayslipsCard';
import { ChildProfileDrawer } from '../components/ChildProfileDrawer';
import { EventsPage } from '../components/EventsPage';
import { MyTransportTab } from '../components/MyTransportTab';
import { MomentComments } from '../components/MomentComments';
import { NotificationPreferencesCard } from '../components/NotificationPreferencesCard';
import { OnboardingChecklist } from '../components/OnboardingChecklist';

function StaffDashboard({ user, organization, onLogout, onChangePassword }) {
  const mobileNav = useMobileNav();
  const [activeTab, setActiveTab] = useState('clock');
  const [staffProfile, setStaffProfile] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [children, setChildren] = useState([]);
  const [clockedIn, setClockedIn] = useState(false);
  const [currentLocation, setCurrentLocation] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Data states for each tab
  const [milestones, setMilestones] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [moments, setMoments] = useState([]);
  const [learningReports, setLearningReports] = useState([]);
  
  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showMomentModal, setShowMomentModal] = useState(false);
  const [showStaffIdModal, setShowStaffIdModal] = useState(false);
  // Incident photo attachments — picked client-side first, uploaded
  // post-create so each photo gets bound to a real incident_id. Cap at
  // INCIDENT_PHOTO_MAX so the user can't queue an unbounded amount.
  const INCIDENT_PHOTO_MAX = 5;
  const INCIDENT_PHOTO_MAX_BYTES = 8 * 1024 * 1024;
  const [incidentPhotoFiles, setIncidentPhotoFiles] = useState([]); // [{ file, previewUrl }]
  const [incidentPhotoBusy, setIncidentPhotoBusy] = useState(false);
  // Shared MessageComposer + unread badge for the new Messages tab.
  const [composerCtx, setComposerCtx] = useState(null);
  const [staffUnreadMessages, setStaffUnreadMessages] = useState(0);

  // ChildProfileDrawer — opened from the Children tab.
  const [childDrawerId, setChildDrawerId] = useState(null);

  // Moment detail viewer — opens a full-size modal when a card in the
  // Moments feed is clicked. Mirrors the admin viewer so staff can see
  // the whole caption/media (bug: previously the feed cards used the
  // wrong fields and had no click behaviour).
  const [viewingMoment, setViewingMoment] = useState(null);
  const [editingMoment, setEditingMoment] = useState(null);
  const [momentActionError, setMomentActionError] = useState('');
  const [momentActionSubmitting, setMomentActionSubmitting] = useState(false);

  // Refresh the unread-message count whenever the user navigates tabs
  // so the bell stays accurate even when the inbox is closed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.getCommunications({ unread_only: true });
        if (cancelled) return;
        setStaffUnreadMessages(Array.isArray(list)
          ? list.filter((m) => m.sent_by_user_id !== user?._id).length
          : 0);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [activeTab, user?._id]);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // Filter data based on search and date range
  const filterData = (data, dateField = 'created_at') => {
    let filtered = [...data];
    
    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(item => 
        JSON.stringify(item).toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Date range filter
    if (startDate) {
      filtered = filtered.filter(item => 
        new Date(item[dateField]) >= new Date(startDate)
      );
    }
    if (endDate) {
      filtered = filtered.filter(item => 
        new Date(item[dateField]) <= new Date(endDate + 'T23:59:59')
      );
    }
    
    return filtered;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Get staff profile
      const profiles = await api.getStaffProfiles();
      const myProfile = profiles.find(p => p.user_id === user._id);
      setStaffProfile(myProfile);

      // Load children for child development features
      const childrenData = await api.getChildren();
      setChildren(childrenData);

      if (activeTab === 'clock') {
        const roomsData = await api.getRooms();
        setRooms(roomsData);

        // BUG FIX: previously we only fetched today's events, which
        // missed an open shift from yesterday (e.g. overtime crossing
        // midnight) and falsely showed the staff member as already
        // signed out. The new /api/clock-events/my-status endpoint
        // looks back 48h and replays events to determine true state.
        try {
          const myStatus = await api.getMyClockStatus();
          setClockedIn(!!myStatus?.clocked_in);
          setCurrentLocation(myStatus?.location || '');
        } catch (_) {
          setClockedIn(false);
        }
      } else if (activeTab === 'milestones') {
        // Load milestones
        const milestonesData = await api.getMilestones();
        setMilestones(milestonesData);
      } else if (activeTab === 'incidents') {
        // Load incidents
        const incidentsData = await api.getIncidents();
        setIncidents(incidentsData);
      } else if (activeTab === 'moments') {
        // Load moments
        const momentsData = await api.getMoments();
        setMoments(momentsData);
      } else if (activeTab === 'reports') {
        // Load learning reports
        const reportsData = await api.getLearningReports();
        setLearningReports(reportsData);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleClockInOut = async () => {
    if (!staffProfile) {
      alert('Staff profile not found. Please contact admin.');
      return;
    }

    try {
      await api.createClockEvent({
        staff_id: staffProfile._id,
        event_type: clockedIn ? 'clock_out' : 'clock_in',
        location: currentLocation
      });
      
      setClockedIn(!clockedIn);
      alert(`Successfully clocked ${clockedIn ? 'out' : 'in'}!`);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleLogConsumable = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      await api.createConsumableLog({
        staff_id: staffProfile._id,
        child_id: formData.get('child_id') || null,
        consumable_type: formData.get('consumable_type'),
        quantity: parseInt(formData.get('quantity')),
        cost_estimate: parseFloat(formData.get('cost_estimate') || 0),
        notes: formData.get('notes')
      });

      alert('Consumable logged successfully!');
      e.target.reset();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleCreateMilestone = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      await api.createMilestone({
        child_id: formData.get('child_id'),
        category: formData.get('category'),
        title: formData.get('title'),
        description: formData.get('description'),
        age_range: formData.get('age_range'),
        status: formData.get('status'),
        staff_notes: formData.get('staff_notes'),
        achieved_date: formData.get('achieved_date') || null
      });

      alert('Milestone logged successfully!');
      setShowMilestoneModal(false);
      e.target.reset();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleCreateIncident = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    setIncidentPhotoBusy(true);
    try {
      // Step 1: create the incident
      const created = await api.createIncident({
        child_id: formData.get('child_id'),
        incident_type: formData.get('incident_type'),
        severity: formData.get('severity'),
        location: formData.get('location'),
        description: formData.get('description'),
        action_taken: formData.get('action_taken'),
        witnesses: formData.get('witnesses') ? formData.get('witnesses').split(',').map(w => w.trim()) : [],
        parent_notified: formData.get('parent_notified') === 'true'
      });
      const incidentId = created?._id || created?.id;

      // Step 2: upload any selected photos sequentially. We tolerate
      // per-file failure so a single bad image doesn't lose the whole
      // report — surface failures via the alert summary at the end.
      const photoFailures = [];
      if (incidentId && incidentPhotoFiles.length > 0) {
        for (const item of incidentPhotoFiles) {
          try {
            await api.uploadIncidentPhoto(incidentId, item.file);
          } catch (uErr) {
            photoFailures.push(`${item.file.name}: ${uErr.message}`);
          }
        }
      }

      if (photoFailures.length > 0) {
        alert(`Incident saved, but some photos failed to attach:\n${photoFailures.join('\n')}`);
      } else {
        alert('Incident reported successfully!');
      }

      // Cleanup preview blob URLs to avoid memory leaks.
      incidentPhotoFiles.forEach(p => { try { URL.revokeObjectURL(p.previewUrl); } catch { /* ignore */ } });
      setIncidentPhotoFiles([]);
      setShowIncidentModal(false);
      e.target.reset();
      // Refresh the incidents list so newly-uploaded photos are visible.
      try {
        const incidentsData = await api.getIncidents();
        setIncidents(incidentsData);
      } catch { /* ignore */ }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setIncidentPhotoBusy(false);
    }
  };

  // Add photos to the pending incident upload queue. Enforces the cap
  // and the per-file size limit before any network round-trip.
  const handleIncidentPhotoPicker = (e) => {
    const picked = Array.from(e.target.files || []);
    try { e.target.value = ''; } catch { /* ignore */ }
    if (!picked.length) return;

    const remaining = INCIDENT_PHOTO_MAX - incidentPhotoFiles.length;
    if (remaining <= 0) {
      alert(`You can attach a maximum of ${INCIDENT_PHOTO_MAX} photos.`);
      return;
    }
    const accepted = [];
    const rejected = [];
    for (const f of picked.slice(0, remaining)) {
      if (f.size > INCIDENT_PHOTO_MAX_BYTES) {
        rejected.push(`${f.name} (>8MB)`);
        continue;
      }
      accepted.push({ file: f, previewUrl: URL.createObjectURL(f) });
    }
    if (picked.length > remaining) {
      rejected.push(`Only ${remaining} more photo${remaining === 1 ? '' : 's'} allowed; extras ignored.`);
    }
    setIncidentPhotoFiles(prev => [...prev, ...accepted]);
    if (rejected.length) alert('Some files were not added:\n' + rejected.join('\n'));
  };

  const handleRemoveIncidentPhoto = (idx) => {
    setIncidentPhotoFiles(prev => {
      const next = [...prev];
      const [removed] = next.splice(idx, 1);
      if (removed?.previewUrl) {
        try { URL.revokeObjectURL(removed.previewUrl); } catch { /* ignore */ }
      }
      return next;
    });
  };

  const handleCreateMessage = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      await api.createCommunication({
        child_id: formData.get('child_id'),
        message_type: formData.get('message_type'),
        subject: formData.get('subject'),
        message: formData.get('message'),
        priority: formData.get('priority')
      });

      alert('Message sent successfully!');
      setShowMessageModal(false);
      e.target.reset();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleCreateReport = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    // Parse areas
    const areasData = [];
    const areaTypes = ['literacy', 'numeracy', 'creative', 'physical', 'social_emotional'];
    areaTypes.forEach(area => {
      const rating = formData.get(`${area}_rating`);
      const notes = formData.get(`${area}_notes`);
      if (rating) {
        areasData.push({
          area: area,
          rating: parseInt(rating),
          notes: notes || ''
        });
      }
    });

    try {
      await api.createLearningReport({
        child_id: formData.get('child_id'),
        period: formData.get('period'),
        period_start: formData.get('period_start'),
        period_end: formData.get('period_end'),
        areas: areasData,
        strengths: formData.get('strengths') ? formData.get('strengths').split('\n').filter(s => s.trim()) : [],
        areas_for_development: formData.get('areas_for_development') ? formData.get('areas_for_development').split('\n').filter(s => s.trim()) : [],
        overall_notes: formData.get('overall_notes')
      });

      alert('Learning report created successfully!');
      setShowReportModal(false);
      e.target.reset();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const [momentPhoto, setMomentPhoto] = useState(null); // {file_id, url, ...} after upload
  const [momentPhotoUploading, setMomentPhotoUploading] = useState(false);
  const [momentPhotoError, setMomentPhotoError] = useState(null);

  const handleMomentPhotoPick = async (file) => {
    if (!file) return;
    setMomentPhotoError(null);
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      setMomentPhotoError('Please choose an image or short video.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMomentPhotoError('File too large (max 10 MB).');
      return;
    }
    setMomentPhotoUploading(true);
    try {
      const result = await api.uploadFile(file, { category: 'moments' });
      setMomentPhoto(result);
    } catch (err) {
      setMomentPhotoError(err.message || 'Upload failed');
    } finally {
      setMomentPhotoUploading(false);
    }
  };

  const handleClearMomentPhoto = async () => {
    if (momentPhoto?.file_id) {
      try { await api.deleteFile(momentPhoto.file_id); } catch { /* best effort */ }
    }
    setMomentPhoto(null);
    setMomentPhotoError(null);
  };

  const handleCreateMoment = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      await api.createMoment({
        child_id: formData.get('child_id'),
        caption: formData.get('caption'),
        tags: formData.get('tags') ? formData.get('tags').split(',').map(t => t.trim()) : [],
        // Use the uploaded S3 presigned URL if present, otherwise fall back
        // to the paste-URL field for backward compatibility.
        photo_url: momentPhoto?.url || formData.get('photo_url') || null,
        media_file_id: momentPhoto?.file_id || null,
      });

      alert('Moment posted successfully!');
      setShowMomentModal(false);
      setMomentPhoto(null);
      setMomentPhotoError(null);
      e.target.reset();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  return (
    <div className="dashboard">
      <button
        type="button"
        className="mobile-nav-toggle"
        onClick={mobileNav.toggle}
        aria-label="Toggle navigation"
        data-testid="mobile-nav-toggle"
      >
        {mobileNav.isOpen ? '✕' : '☰'}
      </button>
      <div className={`mobile-nav-backdrop ${mobileNav.isOpen ? 'show' : ''}`} onClick={mobileNav.close}></div>
      <div className={`sidebar ${mobileNav.isOpen ? 'mobile-open' : ''}`} onClick={(e) => {
        if (e.target.closest('.nav-item')) mobileNav.close();
      }}>
        <div className="sidebar-header" data-testid="sidebar-header">
          {organization && organization.settings && organization.settings.logo_url ? (
            <>
              <img
                src={organization.settings.logo_url}
                alt={organization.name}
                style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain', marginBottom: '0.5rem' }}
                data-testid="sidebar-org-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <h2 style={{ fontSize: '1rem', margin: 0 }}>👋 {organization.name}</h2>
            </>
          ) : (
            <h2>👋 {organization ? organization.name : 'Staff Portal'}</h2>
          )}
          <p>{user.full_name}</p>
          {staffProfile && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
              {staffProfile.competencies && staffProfile.competencies.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.5rem' }}>
                  {staffProfile.competencies.map((comp, i) => (
                    <span key={i} className="badge badge-blue" style={{ fontSize: '0.75rem' }}>
                      {comp.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <ul className="nav-menu">
          <li
            className={`nav-item ${activeTab === 'clock' ? 'active' : ''}`}
            onClick={() => setActiveTab('clock')}
            data-testid="nav-clock"
          >
            🕐 Clock In/Out
          </li>
          <li
            className={`nav-item ${activeTab === 'roster' ? 'active' : ''}`}
            onClick={() => setActiveTab('roster')}
            data-testid="nav-roster"
          >
            🪪 Daily Roster
          </li>
          {/* Unified read-only Children list. Opens ChildProfileDrawer
              with full safety info, parent contact, attendance history,
              moments/reports/milestones/incidents. */}
          <li
            className={`nav-item ${activeTab === 'children' ? 'active' : ''}`}
            onClick={() => setActiveTab('children')}
            data-testid="nav-children"
          >
            👶 Children
          </li>
          <li
            className={`nav-item ${activeTab === 'consumables' ? 'active' : ''}`}
            onClick={() => setActiveTab('consumables')}
            data-testid="nav-consumables"
          >
            📦 Log Consumables
          </li>
          <li
            className={`nav-item ${activeTab === 'milestones' ? 'active' : ''}`}
            onClick={() => setActiveTab('milestones')}
            data-testid="nav-milestones"
          >
            🎯 Milestones
          </li>
          <li
            className={`nav-item ${activeTab === 'incidents' ? 'active' : ''}`}
            onClick={() => setActiveTab('incidents')}
            data-testid="nav-incidents"
          >
            🚑 Incidents
          </li>
          {/* Events — date-bound trips, themed days, visitor sessions.
              All staff can view + run the day-of register + post media;
              room_leader+ can also create/edit/notify parents. */}
          <li
            className={`nav-item ${activeTab === 'events' ? 'active' : ''}`}
            onClick={() => setActiveTab('events')}
            data-testid="nav-events"
          >
            🎪 Events
          </li>
          {/* My Transport — surfaces school-run manifests where this
              staff member is either the assigned driver OR listed as
              an escort. The server filters by user_id automatically,
              so the tab safely renders an empty state when nothing
              applies (no flag needed client-side). */}
          <li
            className={`nav-item ${activeTab === 'my-transport' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-transport')}
            data-testid="nav-my-transport"
          >
            🚐 My Transport
          </li>
          <li
            className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
            data-testid="nav-messages"
          >
            ✉️ Messages{staffUnreadMessages > 0 ? ` (${staffUnreadMessages})` : ''}
          </li>
          <li
            className={`nav-item ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
            data-testid="nav-reports"
          >
            📚 Learning Reports
          </li>
          <li
            className={`nav-item ${activeTab === 'moments' ? 'active' : ''}`}
            onClick={() => setActiveTab('moments')}
            data-testid="nav-moments"
          >
            📸 Moments
          </li>
          <li
            className={`nav-item ${activeTab === 'timesheet' ? 'active' : ''}`}
            onClick={() => setActiveTab('timesheet')}
            data-testid="nav-timesheet"
          >
            🕒 My Timesheet
          </li>
          <li
            className={`nav-item ${activeTab === 'availability' ? 'active' : ''}`}
            onClick={() => setActiveTab('availability')}
            data-testid="nav-availability"
          >
            📅 My Availability
          </li>
          <li
            className={`nav-item ${activeTab === 'leave' ? 'active' : ''}`}
            onClick={() => setActiveTab('leave')}
            data-testid="nav-leave"
          >
            🏖️ My Leave
          </li>
          <li
            className={`nav-item ${activeTab === 'shift-requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('shift-requests')}
            data-testid="nav-shift-requests"
          >
            🔁 Shift Requests
          </li>
          <li
            className={`nav-item ${activeTab === 'compliance' ? 'active' : ''}`}
            onClick={() => setActiveTab('compliance')}
            data-testid="nav-compliance"
          >
            🛡️ My Compliance
          </li>
          <li
            className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
            data-testid="nav-profile"
          >
            👤 My Profile
          </li>
          <li
            className="nav-item"
            onClick={() => setShowStaffIdModal(true)}
            data-testid="nav-staff-id-card"
            title="Show or share my staff ID card"
          >
            🪪 My ID Card
          </li>
          <li
            className="nav-item"
            onClick={onChangePassword}
            data-testid="nav-change-password"
            title="Change your password"
          >
            🔐 Change password
          </li>
          <li
            className="nav-item logout-btn"
            onClick={onLogout}
            data-testid="logout-btn"
          >
            🚪 Logout
          </li>
        </ul>
      </div>

      <div className="main-content">
        {activeTab === 'profile' && (
          <>
            <StaffProfileTab />
            {/* Fine-grained per-event channel picker (push / email)
                so staff can control moment-reply pings from within
                their own profile page. */}
            <div style={{ marginTop: '1.5rem' }}>
              <NotificationPreferencesCard />
            </div>
          </>
        )}
        {activeTab === 'timesheet' && (
          <>
            <div className="page-header">
              <h1>🕒 My Timesheet</h1>
              <p>Hours worked, scheduled hours, and gross pay for the week.</p>
            </div>
            <TimesheetView mode="self" title=" " />
          </>
        )}
        {activeTab === 'availability' && (
          <>
            <div className="page-header">
              <h1>📅 My Availability</h1>
              <p>Tell your admins which days and times you can work.</p>
            </div>
            <div className="card">
              <AvailabilityEditor canEdit={true} title="" />
            </div>
          </>
        )}
        {activeTab === 'leave' && (
          <>
            <div className="page-header">
              <h1>🏖️ My Leave</h1>
              <p>Request holidays, sick days, and other absences.</p>
            </div>
            <LeaveManager mode="staff" />
          </>
        )}
        {activeTab === 'shift-requests' && (
          <>
            <div className="page-header">
              <h1>🔁 Shift Requests</h1>
              <p>Swap shifts with colleagues, ask for cover, or request to drop a shift.</p>
            </div>
            <ShiftRequestsInbox mode="staff" />
          </>
        )}

        {activeTab === 'compliance' && (
          <>
            <div className="page-header">
              <h1>🛡️ My Compliance</h1>
              <p>Upload your DBS, first-aid, food-hygiene, driving licence and other certificates. Your manager reviews each upload before it counts as compliant.</p>
            </div>
            <ComplianceManager mode="self" />
          </>
        )}

        {/* Daily Roster Tab — Chain of Custody */}
        {activeTab === 'roster' && (
          <>
            <div className="page-header">
              <h1>🪪 Daily Roster</h1>
              <p>Today's check-ins, check-outs and chain of custody</p>
            </div>
            <DailyRoster allowEdit={true} />
          </>
        )}

        {/* Clock In/Out Tab */}
        {activeTab === 'clock' && (
          <>
            <div className="page-header">
              <h1>Clock In/Out</h1>
              <p>Track your working hours</p>
            </div>

            {/* Friendly onboarding progress card — auto-hides once every
                item is complete. Only meaningful for staff-tier users;
                the endpoint returns an unusable payload for parents so
                the component fails-quiet. */}
            <OnboardingChecklist
              userName={user?.full_name}
              onNavigateTab={(tab) => setActiveTab(tab)}
              refreshTrigger={activeTab}
            />

            {/* New status card — uses /api/clock-events/my-status (48h
                window) so overtime shifts crossing midnight still show
                the correct clocked-in state. Replaces the legacy
                location-only UI below. */}
            <ClockStatusCard
              rooms={rooms}
              onOpenRegister={() => setActiveTab('roster')}
            />

            {/* Self-serve payslip downloads. Staff see only their own
                payslips here; admins generate/email them from the
                staff profile drawer in the Admin Dashboard. */}
            <MyPayslipsCard />

            <div className="card" style={{ maxWidth: '600px' }}>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ 
                  width: '120px', 
                  height: '120px', 
                  borderRadius: '50%', 
                  background: clockedIn ? 'hsl(120 60% 90%)' : 'hsl(var(--muted))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 2rem',
                  fontSize: '3rem'
                }}>
                  {clockedIn ? '✅' : '⏰'}
                </div>

                <h2 style={{ margin: '0 0 0.5rem 0' }}>
                  {clockedIn ? 'Currently Clocked In' : 'Not Clocked In'}
                </h2>
                {clockedIn && currentLocation && (
                  <p style={{ color: 'hsl(var(--muted-foreground))' }}>
                    Location: {currentLocation}
                  </p>
                )}

                <div className="form-group" style={{ marginTop: '2rem' }}>
                  <label>Select Location/Room</label>
                  <select
                    value={currentLocation}
                    onChange={(e) => setCurrentLocation(e.target.value)}
                    data-testid="location-select"
                  >
                    <option value="">-- Select location --</option>
                    {rooms.map(room => (
                      <option key={room._id} value={room.name}>{room.name}</option>
                    ))}
                    <option value="Office">Office</option>
                    <option value="Kitchen">Kitchen</option>
                    <option value="Outdoor">Outdoor</option>
                  </select>
                </div>

                <button
                  className={`btn ${clockedIn ? 'btn-secondary' : 'btn-primary'}`}
                  onClick={handleClockInOut}
                  disabled={!currentLocation}
                  style={{ marginTop: '2rem', fontSize: '1.25rem', padding: '1rem 3rem' }}
                  data-testid="clock-in-out-btn"
                >
                  {clockedIn ? '⏹️ Clock Out' : '▶️ Clock In'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Children Tab — unified read-only profile browser */}
        {activeTab === 'children' && (
          <ChildrenBrowser onOpen={(id) => setChildDrawerId(id)} />
        )}

        {/* Consumables Tab */}
        {activeTab === 'consumables' && (
          <>
            <div className="page-header">
              <h1>Log Consumables</h1>
              <p>Track snacks, meals, and supplies</p>
            </div>

            <div className="card" style={{ maxWidth: '600px' }}>
              <form onSubmit={handleLogConsumable}>
                <div className="form-group">
                  <label>Type</label>
                  <select name="consumable_type" required data-testid="consumable-type">
                    <option value="snack">Snack</option>
                    <option value="meal">Meal</option>
                    <option value="drink">Drink</option>
                    <option value="nappy">Nappy</option>
                    <option value="wipes">Wipes</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>For Child (optional)</label>
                  <select name="child_id" data-testid="consumable-child">
                    <option value="">General/Multiple</option>
                    {children.map(child => (
                      <option key={child._id} value={child._id}>
                        {child.first_name} {child.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    name="quantity"
                    type="number"
                    defaultValue="1"
                    min="1"
                    required
                    data-testid="consumable-quantity"
                  />
                </div>

                <div className="form-group">
                  <label>Estimated Cost (£)</label>
                  <input
                    name="cost_estimate"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    data-testid="consumable-cost"
                  />
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    name="notes"
                    rows="3"
                    placeholder="Optional notes..."
                    data-testid="consumable-notes"
                  />
                </div>

                <button type="submit" className="btn btn-primary" data-testid="submit-consumable">
                  Log Consumable
                </button>
              </form>
            </div>
          </>
        )}

        {/* Milestones Tab */}
        {activeTab === 'milestones' && (
          <>
            <div className="page-header">
              <h1>Development Milestones</h1>
              <p>Track children's development progress</p>
              <button 
                className="btn btn-primary" 
                onClick={() => setShowMilestoneModal(true)}
                data-testid="add-milestone-btn"
              >
                + Add Milestone
              </button>
            </div>

            {/* Filters */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Search</label>
                  <input
                    type="text"
                    placeholder="Search milestones..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>From Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    data-testid="start-date-input"
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>To Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    data-testid="end-date-input"
                  />
                </div>
                {(searchQuery || startDate || endDate) && (
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setSearchQuery('');
                        setStartDate('');
                        setEndDate('');
                      }}
                      data-testid="clear-filters-btn"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading milestones...</div>
            ) : filterData(milestones, 'observed_date').length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {filterData(milestones, 'observed_date').map((milestone) => (
                  <div key={milestone._id} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: '0 0 0.5rem 0' }}>{milestone.child_name}</h3>
                        <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                          {milestone.area} • {milestone.age_category}
                        </div>
                        <p style={{ margin: '0.5rem 0' }}>{milestone.description}</p>
                        {milestone.notes && (
                          <div style={{ 
                            marginTop: '0.75rem', 
                            padding: '0.75rem', 
                            background: 'hsl(var(--muted))', 
                            borderRadius: '6px',
                            fontSize: '0.875rem'
                          }}>
                            <strong>Notes:</strong> {milestone.notes}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', marginLeft: '1rem' }}>
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                          {new Date(milestone.observed_date).toLocaleDateString()}
                        </div>
                        <span className={`badge ${milestone.achieved ? 'badge-green' : 'badge-yellow'}`}>
                          {milestone.achieved ? '✓ Achieved' : 'In Progress'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filterData(milestones, 'observed_date').length === 0 && (searchQuery || startDate || endDate) ? (
              <div className="empty-state">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                <h3>No Results Found</h3>
                <p>Try adjusting your filters</p>
              </div>
            ) : (
              <div className="empty-state">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎯</div>
                <h3>No Milestones Recorded</h3>
                <p>Click "Add Milestone" to log a child's development milestone</p>
              </div>
            )}
          </>
        )}

        {/* Incidents Tab */}
        {activeTab === 'incidents' && (
          <>
            <div className="page-header">
              <h1>Incident Reports</h1>
              <p>Document and report incidents</p>
              <button 
                className="btn btn-primary" 
                onClick={() => setShowIncidentModal(true)}
                data-testid="add-incident-btn"
              >
                + Report Incident
              </button>
            </div>

            {/* Filters */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Search</label>
                  <input
                    type="text"
                    placeholder="Search incidents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>From Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>To Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                {(searchQuery || startDate || endDate) && (
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setSearchQuery('');
                        setStartDate('');
                        setEndDate('');
                      }}
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading incidents...</div>
            ) : filterData(incidents, 'incident_time').length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {filterData(incidents, 'incident_time').map((incident) => (
                  <div key={incident._id} className="card" style={{ 
                    borderLeft: `4px solid ${
                      incident.severity === 'critical' ? 'hsl(0 70% 50%)' :
                      incident.severity === 'moderate' ? 'hsl(45 92% 50%)' :
                      'hsl(220 90% 60%)'
                    }`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <h3 style={{ margin: 0 }}>{incident.child_name}</h3>
                          <span className={`badge ${
                            incident.severity === 'critical' ? 'badge-red' :
                            incident.severity === 'moderate' ? 'badge-yellow' :
                            'badge-blue'
                          }`}>
                            {incident.severity}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
                          {incident.category}
                        </div>
                        <p style={{ margin: '0.5rem 0' }}><strong>Description:</strong> {incident.description}</p>
                        <p style={{ margin: '0.5rem 0', fontSize: '0.875rem' }}>
                          <strong>Action Taken:</strong> {incident.action_taken}
                        </p>
                        {Array.isArray(incident.photo_urls) && incident.photo_urls.length > 0 && (
                          <div
                            data-testid={`incident-photos-${incident._id}`}
                            style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}
                          >
                            {incident.photo_urls.map((url, i) => (
                              <a
                                key={i}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open full-size photo"
                                data-testid={`incident-photo-${incident._id}-${i}`}
                              >
                                <img
                                  src={url}
                                  alt={`Incident photo ${i + 1}`}
                                  style={{
                                    width: 64, height: 64, borderRadius: 6,
                                    objectFit: 'cover', display: 'block',
                                    border: '1px solid hsl(var(--border))',
                                  }}
                                />
                              </a>
                            ))}
                          </div>
                        )}
                        {incident.parent_notified && (
                          <div style={{ 
                            marginTop: '0.75rem',
                            fontSize: '0.875rem',
                            color: 'hsl(145 70% 35%)'
                          }}>
                            ✓ Parent notified
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', marginLeft: '1rem' }}>
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                          {new Date(incident.incident_time).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filterData(incidents, 'incident_time').length === 0 && (searchQuery || startDate || endDate) ? (
              <div className="empty-state">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                <h3>No Results Found</h3>
                <p>Try adjusting your filters</p>
              </div>
            ) : (
              <div className="empty-state">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚑</div>
                <h3>No Incidents Reported</h3>
                <p>Click "Report Incident" to document an incident</p>
              </div>
            )}
          </>
        )}

        {/* Messages Tab — full inbox + composer */}
        {activeTab === 'messages' && (
          <MessagesInbox
            user={user}
            onCompose={() => setComposerCtx({})}
          />
        )}

        {/* Events Tab — date-bound trips / themed days / visitor sessions.
            All staff can view, run the day-of register, and post media.
            room_leader+ can also create / edit / add attendees / notify
            parents (UI gates admin-only actions via canManage). */}
        {activeTab === 'events' && (
          <EventsPage currentUser={user} />
        )}

        {/* My Transport — driver-or-escort view of today's route manifests. */}
        {activeTab === 'my-transport' && (
          <MyTransportTab user={user} />
        )}

        {/* Learning Reports Tab */}
        {activeTab === 'reports' && (
          <>
            <div className="page-header">
              <h1>Learning Reports</h1>
              <p>Create comprehensive learning assessments</p>
              <button 
                className="btn btn-primary" 
                onClick={() => setShowReportModal(true)}
                data-testid="add-report-btn"
              >
                + Create Report
              </button>
            </div>

            {loading ? (
              <div className="loading">Loading reports...</div>
            ) : learningReports.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {learningReports.map((report) => (
                  <div key={report._id} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: '0 0 0.5rem 0' }}>{report.child_name}</h3>
                        <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
                          Period: {new Date(report.period_start).toLocaleDateString()} - {new Date(report.period_end).toLocaleDateString()}
                        </div>
                        
                        {/* Areas of development */}
                        {report.areas && report.areas.length > 0 && (
                          <div style={{ marginTop: '1rem' }}>
                            <h4 style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>Development Areas:</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {report.areas.map((area, idx) => (
                                <span key={idx} className="badge badge-blue">
                                  {area.name}: {area.progress}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {report.summary && (
                          <div style={{ 
                            marginTop: '1rem', 
                            padding: '1rem', 
                            background: 'hsl(var(--muted))', 
                            borderRadius: '6px'
                          }}>
                            <strong>Summary:</strong> {report.summary}
                          </div>
                        )}
                        
                        {report.next_steps && (
                          <div style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
                            <strong>Next Steps:</strong> {report.next_steps}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', marginLeft: '1rem' }}>
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                          {new Date(report.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
                <h3>No Learning Reports</h3>
                <p>Click "Create Report" to generate a learning progress report</p>
              </div>
            )}
          </>
        )}

        {/* Moments Tab */}
        {activeTab === 'moments' && (
          <>
            <div className="page-header">
              <h1>Moments Feed</h1>
              <p>Share special moments with parents</p>
              <button 
                className="btn btn-primary" 
                onClick={() => setShowMomentModal(true)}
                data-testid="add-moment-btn"
              >
                + Post Moment
              </button>
            </div>

            {loading ? (
              <div className="loading">Loading moments...</div>
            ) : moments.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                {moments.map((moment) => {
                  // Field mapping bug fix: backend/admin use `photo_url`
                  // + `caption`, not `image_url` + `description`. Support
                  // both so this list still works during rollout.
                  const mediaUrl = moment.photo_url || moment.image_url || null;
                  const captionText = moment.caption || moment.description || '';
                  const contentType = moment.photo_content_type || '';
                  const isVideo = contentType.startsWith('video/');
                  return (
                    <div
                      key={moment._id}
                      className="card"
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setMomentActionError('');
                        setEditingMoment(null);
                        setViewingMoment(moment);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setMomentActionError('');
                          setEditingMoment(null);
                          setViewingMoment(moment);
                        }
                      }}
                      data-testid={`moment-card-${moment._id}`}
                      style={{ cursor: 'pointer' }}
                    >
                      {mediaUrl && (
                        isVideo ? (
                          <video
                            src={mediaUrl}
                            controls={false}
                            muted
                            playsInline
                            preload="metadata"
                            data-testid={`moment-card-video-${moment._id}`}
                            style={{
                              width: '100%',
                              height: '200px',
                              objectFit: 'cover',
                              borderRadius: '8px 8px 0 0',
                              marginBottom: '1rem',
                              background: '#000',
                              display: 'block',
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '200px',
                            background: `url(${mediaUrl}) center/cover`,
                            borderRadius: '8px 8px 0 0',
                            marginBottom: '1rem',
                          }} />
                        )
                      )}
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <h3 style={{ margin: 0, fontSize: '1rem' }}>{moment.child_name}</h3>
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                            {new Date(moment.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        {moment.activity && (
                          <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                            {moment.activity}
                          </div>
                        )}
                        {captionText && (
                          <p
                            style={{
                              margin: '0.5rem 0',
                              fontSize: '0.875rem',
                              // Clamp to 3 lines on cards; full view in modal.
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                            data-testid={`moment-card-caption-${moment._id}`}
                          >
                            {captionText}
                          </p>
                        )}
                        {moment.tags && moment.tags.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                            {moment.tags.map((tag, idx) => (
                              <span key={idx} className="badge badge-blue" style={{ fontSize: '0.75rem' }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                          <span>Tap to view full moment →</span>
                          {moment.comment_count > 0 && (
                            <span data-testid={`staff-moment-card-comment-count-${moment._id}`}>
                              💬 {moment.comment_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📸</div>
                <h3>No Moments Shared</h3>
                <p>Click "Post Moment" to share a special moment with parents</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Milestone Modal */}
      {showMilestoneModal && (
        <div className="modal-overlay" onClick={() => setShowMilestoneModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="milestone-modal">
            <div className="modal-header">
              <h2>Add Development Milestone</h2>
              <button className="modal-close" onClick={() => setShowMilestoneModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateMilestone}>
              <div className="form-group">
                <label>Child *</label>
                <select name="child_id" required data-testid="milestone-child">
                  <option value="">-- Select child --</option>
                  {children.map(child => (
                    <option key={child._id} value={child._id}>
                      {child.first_name} {child.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select name="category" required data-testid="milestone-category">
                  <option value="physical">Physical Development</option>
                  <option value="cognitive">Cognitive Development</option>
                  <option value="social">Social Development</option>
                  <option value="emotional">Emotional Development</option>
                  <option value="language">Language Development</option>
                </select>
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input name="title" type="text" required placeholder="e.g., First steps" data-testid="milestone-title" />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea name="description" rows="3" placeholder="Describe the milestone..." data-testid="milestone-description" />
              </div>

              <div className="form-group">
                <label>Age Range</label>
                <input name="age_range" type="text" placeholder="e.g., 12-18 months" data-testid="milestone-age-range" />
              </div>

              <div className="form-group">
                <label>Status *</label>
                <select name="status" required data-testid="milestone-status">
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="achieved">Achieved</option>
                </select>
              </div>

              <div className="form-group">
                <label>Staff Notes</label>
                <textarea name="staff_notes" rows="3" placeholder="Any observations or notes..." data-testid="milestone-notes" />
              </div>

              <div className="form-group">
                <label>Achieved Date (if achieved)</label>
                <input name="achieved_date" type="date" data-testid="milestone-date" />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn btn-primary" data-testid="milestone-submit">Save Milestone</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowMilestoneModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Incident Modal */}
      {showIncidentModal && (
        <div className="modal-overlay" onClick={() => setShowIncidentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="incident-modal">
            <div className="modal-header">
              <h2>Report Incident</h2>
              <button className="modal-close" onClick={() => setShowIncidentModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateIncident}>
              <div className="form-group">
                <label>Child *</label>
                <select name="child_id" required data-testid="incident-child">
                  <option value="">-- Select child --</option>
                  {children.map(child => (
                    <option key={child._id} value={child._id}>
                      {child.first_name} {child.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Incident Type *</label>
                <select name="incident_type" required data-testid="incident-type">
                  <option value="injury">Injury</option>
                  <option value="illness">Illness</option>
                  <option value="behavioral">Behavioral</option>
                  <option value="accident">Accident</option>
                  <option value="allergy">Allergy Reaction</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Severity *</label>
                <select name="severity" required data-testid="incident-severity">
                  <option value="minor">Minor</option>
                  <option value="moderate">Moderate</option>
                  <option value="serious">Serious</option>
                  <option value="emergency">Emergency</option>
                </select>
              </div>

              <div className="form-group">
                <label>Location *</label>
                <input name="location" type="text" required placeholder="Where did this occur?" data-testid="incident-location" />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea name="description" rows="4" required placeholder="Describe what happened..." data-testid="incident-description" />
              </div>

              <div className="form-group">
                <label>Action Taken *</label>
                <textarea name="action_taken" rows="4" required placeholder="What actions were taken?" data-testid="incident-action" />
              </div>

              <div className="form-group">
                <label>Witnesses (comma-separated)</label>
                <input name="witnesses" type="text" placeholder="e.g., Jane Doe, John Smith" data-testid="incident-witnesses" />
              </div>

              <div className="form-group">
                <label>Parent Notified? *</label>
                <select name="parent_notified" required data-testid="incident-notified">
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              {/* Photo attachments — up to 5 evidence shots per report.
                  Picked client-side first, uploaded post-create so each
                  file is bound to a real incident_id by the backend.
                  Per-file remove preview lets staff fix mistakes before
                  submitting. */}
              <div className="form-group" data-testid="incident-photos-block">
                <label>
                  Photos
                  <span style={{ fontWeight: 400, color: 'hsl(var(--muted-foreground))', marginLeft: '0.4rem', fontSize: '0.85rem' }}>
                    (optional · up to {INCIDENT_PHOTO_MAX}, max 8MB each)
                  </span>
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.4rem' }}>
                  {incidentPhotoFiles.map((p, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: 'relative', width: 72, height: 72, borderRadius: 8,
                        overflow: 'hidden', border: '1px solid hsl(var(--border))',
                        background: 'hsl(var(--muted))',
                      }}
                      data-testid={`incident-photo-thumb-${idx}`}
                    >
                      <img
                        src={p.previewUrl}
                        alt={`Incident photo ${idx + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveIncidentPhoto(idx)}
                        data-testid={`remove-incident-photo-${idx}`}
                        title="Remove photo"
                        style={{
                          position: 'absolute', top: 2, right: 2,
                          width: 22, height: 22, borderRadius: '50%',
                          background: 'rgba(0,0,0,0.7)', color: '#fff',
                          border: 'none', cursor: 'pointer', fontSize: 14,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: 0, lineHeight: 1,
                        }}
                      >×</button>
                    </div>
                  ))}
                  {incidentPhotoFiles.length < INCIDENT_PHOTO_MAX && (
                    <label
                      style={{
                        width: 72, height: 72, borderRadius: 8,
                        border: '2px dashed hsl(var(--border))',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: incidentPhotoBusy ? 'wait' : 'pointer',
                        color: 'hsl(var(--muted-foreground))', fontSize: '1.4rem',
                        background: 'hsl(var(--muted) / 0.3)',
                      }}
                      title="Add photo"
                      data-testid="add-incident-photo-btn"
                    >
                      📷
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
                        multiple
                        onChange={handleIncidentPhotoPicker}
                        disabled={incidentPhotoBusy}
                        data-testid="incident-photo-input"
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.35rem' }}>
                  {incidentPhotoFiles.length}/{INCIDENT_PHOTO_MAX} photo{incidentPhotoFiles.length === 1 ? '' : 's'} selected
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  data-testid="incident-submit"
                  disabled={incidentPhotoBusy}
                >
                  {incidentPhotoBusy ? 'Saving…' : 'Submit Report'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    // Free preview blob URLs and reset the photo queue
                    // when the user cancels — keeps memory clean across
                    // repeated open/close cycles.
                    incidentPhotoFiles.forEach(p => { try { URL.revokeObjectURL(p.previewUrl); } catch { /* ignore */ } });
                    setIncidentPhotoFiles([]);
                    setShowIncidentModal(false);
                  }}
                  disabled={incidentPhotoBusy}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <div className="modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="message-modal">
            <div className="modal-header">
              <h2>Send Message to Parent</h2>
              <button className="modal-close" onClick={() => setShowMessageModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateMessage}>
              <div className="form-group">
                <label>Child *</label>
                <select name="child_id" required data-testid="message-child">
                  <option value="">-- Select child --</option>
                  {children.map(child => (
                    <option key={child._id} value={child._id}>
                      {child.first_name} {child.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Message Type *</label>
                <select name="message_type" required data-testid="message-type">
                  <option value="general">General Update</option>
                  <option value="behavior">Behavior Update</option>
                  <option value="health">Health Update</option>
                  <option value="achievement">Achievement</option>
                  <option value="reminder">Reminder</option>
                  <option value="request">Request</option>
                </select>
              </div>

              <div className="form-group">
                <label>Priority *</label>
                <select name="priority" required data-testid="message-priority">
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="form-group">
                <label>Subject *</label>
                <input name="subject" type="text" required placeholder="Message subject" data-testid="message-subject" />
              </div>

              <div className="form-group">
                <label>Message *</label>
                <textarea name="message" rows="6" required placeholder="Type your message here..." data-testid="message-content" />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn btn-primary" data-testid="message-submit">Send Message</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowMessageModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Learning Report Modal */}
      {showReportModal && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="report-modal"
            ref={(el) => { if (el) el.scrollTop = 0; }}
          >
            <div className="modal-header" style={{ position: 'sticky', top: 0, background: 'hsl(var(--card))', zIndex: 5, borderBottom: '1px solid hsl(var(--border))', padding: '0.75rem 1rem' }}>
              <h2 style={{ margin: 0 }}>Create Learning Report</h2>
              <button className="modal-close" onClick={() => setShowReportModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateReport} style={{ padding: '1rem' }}>
              {/* Child + period — kept at the top so staff always know
                  who the report is for. Highlighted in case a previous
                  scroll position lands them mid-form. */}
              <div className="form-group" style={{
                background: 'hsl(45 95% 96%)',
                border: '1px solid hsl(45 90% 75%)',
                padding: '0.85rem',
                borderRadius: 6,
                marginBottom: '1rem',
              }}>
                <label style={{ fontWeight: 700 }}>👶 Which child is this report for? *</label>
                <select name="child_id" required data-testid="report-child" style={{ marginTop: 4 }}>
                  <option value="">— Select a child —</option>
                  {children.map(child => (
                    <option key={child._id} value={child._id}>
                      {child.first_name} {child.last_name}
                    </option>
                  ))}
                </select>
                {children.length === 0 && (
                  <small style={{ color: 'hsl(var(--destructive))', display: 'block', marginTop: 4 }}>
                    No children available — ask your administrator to add children to your setting first.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Period *</label>
                <select name="period" required data-testid="report-period">
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Period Start *</label>
                  <input name="period_start" type="date" required data-testid="report-start" />
                </div>
                <div className="form-group">
                  <label>Period End *</label>
                  <input name="period_end" type="date" required data-testid="report-end" />
                </div>
              </div>

              <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Learning Areas</h3>
              
              {['literacy', 'numeracy', 'creative', 'physical', 'social_emotional'].map(area => (
                <div key={area} style={{ marginBottom: '1rem', padding: '1rem', background: 'hsl(var(--muted))', borderRadius: '6px' }}>
                  <h4 style={{ textTransform: 'capitalize', marginBottom: '0.5rem' }}>{area.replace('_', ' ')}</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Rating (1-5)</label>
                      <input name={`${area}_rating`} type="number" min="1" max="5" placeholder="1-5" data-testid={`report-${area}-rating`} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Notes</label>
                      <input name={`${area}_notes`} type="text" placeholder="Optional notes" data-testid={`report-${area}-notes`} />
                    </div>
                  </div>
                </div>
              ))}

              <div className="form-group">
                <label>Strengths (one per line)</label>
                <textarea name="strengths" rows="3" placeholder="List the child's strengths..." data-testid="report-strengths" />
              </div>

              <div className="form-group">
                <label>Areas for Development (one per line)</label>
                <textarea name="areas_for_development" rows="3" placeholder="List areas for growth..." data-testid="report-development" />
              </div>

              <div className="form-group">
                <label>Overall Notes</label>
                <textarea name="overall_notes" rows="4" placeholder="General observations and comments..." data-testid="report-notes" />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn btn-primary" data-testid="report-submit">Create Report</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowReportModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Moment Modal */}
      {showMomentModal && (
        <div className="modal-overlay" onClick={() => setShowMomentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="moment-modal">
            <div className="modal-header">
              <h2>Post a Moment</h2>
              <button className="modal-close" onClick={() => setShowMomentModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateMoment}>
              <div className="form-group">
                <label>Child *</label>
                <select name="child_id" required data-testid="moment-child">
                  <option value="">-- Select child --</option>
                  {children.map(child => (
                    <option key={child._id} value={child._id}>
                      {child.first_name} {child.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Caption *</label>
                <textarea name="caption" rows="4" required placeholder="Describe this special moment..." data-testid="moment-caption" />
              </div>

              <div className="form-group">
                <label>Photo or Video (optional)</label>
                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start',
                  padding: '0.75rem',
                  border: '1px dashed hsl(var(--border))',
                  borderRadius: 'var(--radius)',
                  background: 'hsl(var(--muted) / 0.3)',
                }}>
                  <div style={{
                    width: 96, height: 96, borderRadius: 'var(--radius)',
                    background: '#fff', border: '1px solid hsl(var(--border))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', flexShrink: 0,
                  }}>
                    {momentPhoto?.url && momentPhoto.content_type?.startsWith('image/') ? (
                      <img src={momentPhoto.url} alt="preview"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
                        data-testid="moment-photo-preview" />
                    ) : momentPhoto?.url ? (
                      <span style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', padding: '0.25rem', textAlign: 'center' }}>
                        📹 Video ready
                      </span>
                    ) : (
                      <span style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', padding: '0.25rem', textAlign: 'center' }}>
                        No media
                      </span>
                    )}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <label
                        className="btn btn-primary"
                        style={{ cursor: momentPhotoUploading ? 'wait' : 'pointer', margin: 0 }}
                        data-testid="moment-upload-label"
                      >
                        {momentPhotoUploading ? 'Uploading…' : (momentPhoto ? '🔄 Replace' : '⬆️ Upload')}
                        <input
                          type="file"
                          accept="image/*,video/mp4,video/quicktime,video/webm"
                          style={{ display: 'none' }}
                          disabled={momentPhotoUploading}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            handleMomentPhotoPick(f);
                            e.target.value = '';
                          }}
                          data-testid="moment-file-input"
                        />
                      </label>
                      {momentPhoto && (
                        <button type="button" className="btn btn-ghost"
                          onClick={handleClearMomentPhoto}
                          disabled={momentPhotoUploading}
                          data-testid="moment-clear-media">
                          🗑️ Remove
                        </button>
                      )}
                    </div>
                    <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                      Images or short videos (MP4, MOV, WEBM) · max 10 MB.
                    </small>
                    {momentPhotoError && (
                      <div style={{
                        fontSize: '0.8rem', color: '#9f1239',
                        background: '#fff1f2', border: '1px solid #fecdd3',
                        borderRadius: '0.375rem', padding: '0.4rem 0.6rem',
                      }} data-testid="moment-upload-error">{momentPhotoError}</div>
                    )}
                    <details>
                      <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                        Or paste a hosted image URL
                      </summary>
                      <input name="photo_url" type="url" placeholder="https://example.com/photo.jpg"
                        data-testid="moment-photo" style={{ marginTop: '0.5rem' }} />
                    </details>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Tags (comma-separated)</label>
                <input name="tags" type="text" placeholder="e.g., outdoor, art, learning" data-testid="moment-tags" />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn btn-primary" data-testid="moment-submit">Post Moment</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowMomentModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shared MessageComposer — opened by Messages tab "New message" CTA. */}
      <MessageComposer
        open={!!composerCtx}
        onClose={() => setComposerCtx(null)}
        user={user}
        childId={composerCtx?.childId || null}
        childName={composerCtx?.childName || null}
        onSent={() => {
          setComposerCtx(null);
          // Re-fetch unread count so the nav badge stays in sync.
          (async () => {
            try {
              const list = await api.getCommunications({ unread_only: true });
              setStaffUnreadMessages(Array.isArray(list)
                ? list.filter((m) => m.sent_by_user_id !== user?._id).length
                : 0);
            } catch (_) { /* non-fatal */ }
          })();
        }}
      />

      {/* Note: The floating Clock/Register pill was removed from the Staff
          dashboard on user feedback — staff said the bottom-right button
          was hovering over the Daily Roster action area and preventing
          them from signing children in. The canonical entry points are
          now the side-nav items "🕒 Clock In/Out" and "🧒 Children's
          Register", which remain present at all times. */}

      {/* Unified child profile drawer — opens from the Children tab. */}
      <ChildProfileDrawer
        open={!!childDrawerId}
        childId={childDrawerId}
        onClose={() => setChildDrawerId(null)}
      />
      {/* Staff ID badge modal — opens from the side-nav "🪪 My ID Card". */}
      <StaffIDCardModal
        open={showStaffIdModal}
        onClose={() => setShowStaffIdModal(false)}
      />

      {/* Moment detail modal — full media + full caption + tags. Mirrors
          the Admin viewer so staff can see the same complete view as
          admins (bug fix: previously only cropped card view was shown).
          Editing/deleting is delegated to Admin/Room Leader flows on the
          Admin dashboard; here it's read-only. */}
      {viewingMoment && (() => {
        const vm = viewingMoment;
        const contentType = vm.photo_content_type || '';
        const isVideo = contentType.startsWith('video/');
        return (
          <div
            className="modal-overlay"
            onClick={() => { setViewingMoment(null); setEditingMoment(null); setMomentActionError(''); }}
            data-testid="staff-moment-detail-modal"
          >
            <div
              className="modal"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 720, width: '100%' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div>
                  <h2 style={{ margin: 0 }}>📸 Moment</h2>
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {vm.child_name || 'Unknown child'} • posted by {vm.posted_by_name || 'unknown'} •{' '}
                    {new Date(vm.created_at).toLocaleString()}
                    {vm.edited_at && (
                      <>
                        {' • '}
                        <span style={{ fontStyle: 'italic' }} data-testid="staff-moment-detail-edited-badge">
                          edited {new Date(vm.edited_at).toLocaleString()}
                        </span>
                      </>
                    )}
                  </small>
                </div>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => { setViewingMoment(null); setEditingMoment(null); setMomentActionError(''); }}
                  data-testid="staff-moment-detail-close"
                  aria-label="Close moment detail"
                >
                  ×
                </button>
              </div>

              {/* Full-size media */}
              {vm.photo_url ? (
                <div style={{ margin: '0.75rem 0', background: '#000', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                  {isVideo ? (
                    <video
                      src={vm.photo_url}
                      controls
                      preload="metadata"
                      data-testid="staff-moment-detail-video"
                      style={{ width: '100%', maxHeight: '60vh', display: 'block', background: '#000' }}
                    />
                  ) : (
                    <img
                      src={vm.photo_url}
                      alt={vm.caption || 'Moment media'}
                      data-testid="staff-moment-detail-image"
                      style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain', background: '#000', display: 'block' }}
                    />
                  )}
                </div>
              ) : (
                <div
                  style={{
                    margin: '0.75rem 0',
                    padding: '1rem',
                    border: '1px dashed hsl(var(--border))',
                    borderRadius: 'var(--radius)',
                    color: 'hsl(var(--muted-foreground))',
                    fontSize: '0.85rem',
                    textAlign: 'center',
                  }}
                  data-testid="staff-moment-detail-no-media"
                >
                  No photo or video attached to this moment.
                </div>
              )}

              {/* Full caption */}
              {(vm.caption || vm.description) && (
                <p
                  style={{ margin: '0.5rem 0', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                  data-testid="staff-moment-detail-caption"
                >
                  {vm.caption || vm.description}
                </p>
              )}

              {/* Tags */}
              {vm.tags && vm.tags.length > 0 && (
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', margin: '0.5rem 0 0.75rem' }}>
                  {vm.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="badge badge-blue"
                      data-testid={`staff-moment-detail-tag-${i}`}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {momentActionError && (
                <div
                  className="alert alert-error"
                  data-testid="staff-moment-detail-error"
                  style={{
                    color: 'hsl(0 70% 35%)',
                    background: 'hsl(0 70% 95%)',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 6,
                    marginBottom: '0.5rem',
                    fontSize: '0.85rem',
                  }}
                >
                  {momentActionError}
                </div>
              )}

              {/* Comment thread — staff can reply to parent comments and
                  parents' replies land here in real time. */}
              <MomentComments
                momentId={vm._id}
                currentUser={user}
                onCountChange={(n) => {
                  setMoments((prev) =>
                    (prev || []).map((x) => (x._id === vm._id ? { ...x, comment_count: n } : x))
                  );
                  setViewingMoment((cur) => (cur && cur._id === vm._id ? { ...cur, comment_count: n } : cur));
                }}
              />

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => { setViewingMoment(null); setEditingMoment(null); setMomentActionError(''); }}
                  data-testid="staff-moment-detail-done"
                  disabled={momentActionSubmitting}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/**
 * ChildrenBrowser — Searchable list rendered inside the Children tab of
 * StaffDashboard. Calls /api/staff/children (center-scoped) and lets the
 * staff member open any child to see their full profile in the drawer.
 */
function ChildrenBrowser({ onOpen }) {
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.staffListChildren()
      .then((data) => { if (!cancelled) setChildren(Array.isArray(data) ? data : []); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load children'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return children;
    return children.filter((c) =>
      (c.full_name || '').toLowerCase().includes(needle)
      || (c.first_name || '').toLowerCase().includes(needle)
      || (c.last_name || '').toLowerCase().includes(needle)
      || (c.year_group || '').toLowerCase().includes(needle)
    );
  }, [children, search]);

  return (
    <div data-testid="children-browser">
      <div className="page-header">
        <h1>👶 Children</h1>
        <p>Open any child to see their full profile, parent contact, safety info, attendance and activity.</p>
      </div>

      <div style={{ marginBottom: '1rem', maxWidth: 480 }}>
        <input
          type="search"
          placeholder="Search by name or year group…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="children-browser-search"
          style={{
            width: '100%',
            padding: '0.6rem 0.8rem',
            border: '1px solid hsl(var(--border))',
            borderRadius: 'var(--radius)',
            background: '#fff',
            fontSize: '0.95rem',
          }}
        />
      </div>

      {loading ? (
        <div className="loading">Loading children…</div>
      ) : error ? (
        <div className="card" style={{ borderLeft: '4px solid hsl(0 60% 50%)', color: 'hsl(0 60% 40%)' }} data-testid="children-browser-error">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state" data-testid="children-browser-empty">
          <p>{search ? `No children match "${search}".` : 'No children in your center yet.'}</p>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '0.85rem',
          }}
          data-testid="children-browser-grid"
        >
          {filtered.map((c) => (
            <button
              key={c._id}
              type="button"
              onClick={() => onOpen(c._id)}
              data-testid={`children-card-${c._id}`}
              style={{
                textAlign: 'left',
                padding: '0.85rem',
                border: '1px solid hsl(var(--border))',
                borderRadius: 'var(--radius)',
                background: '#fff',
                cursor: 'pointer',
                transition: 'transform 120ms ease, box-shadow 120ms ease',
                display: 'flex',
                gap: '0.7rem',
                alignItems: 'center',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div
                style={{
                  width: 48, height: 48, borderRadius: '50%', overflow: 'hidden',
                  background: 'hsl(var(--muted))', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flex: '0 0 auto', border: '1px solid hsl(var(--border))', fontSize: '1.4rem',
                }}
              >
                {c.photo_url ? (
                  <img src={c.photo_url} alt={c.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : '👶'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{c.full_name || '—'}</div>
                <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                  {c.year_group || '—'}
                </div>
                <div style={{ marginTop: '0.3rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                  {c.allergies_count > 0 && (
                    <span style={{ fontSize: '0.7rem', background: 'hsl(0 75% 92%)', color: 'hsl(0 60% 30%)', padding: '0.1rem 0.4rem', borderRadius: 999, fontWeight: 600 }}>
                      🥜 {c.allergies_count}
                    </span>
                  )}
                  {c.has_send && (
                    <span style={{ fontSize: '0.7rem', background: 'hsl(260 60% 92%)', color: 'hsl(260 60% 30%)', padding: '0.1rem 0.4rem', borderRadius: 999, fontWeight: 600 }}>
                      🧩 SEND
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default StaffDashboard;
